import React, { useState, useEffect, useCallback } from 'react';
import {
  Plus,
  RefreshCw,
  ClipboardCheck,
  Calendar,
  ChevronDown,
  ChevronUp,
  X,
  AlertTriangle,
  Search,
  User,
} from 'lucide-react';

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

type AuditType = 'internal' | 'surveillance' | 'recertification' | 'supplier' | 'process' | 'follow_up';
type AuditStatus = 'planned' | 'in_progress' | 'fieldwork' | 'reporting' | 'management_review' | 'closed';

interface Audit {
  id: number;
  title: string;
  objective: string;
  scope_description: string;
  criteria: string;
  audit_type: AuditType;
  status: AuditStatus;
  planned_start: string | null;
  planned_end: string | null;
  lead_auditor_name?: string;
  findings_count?: number;
  checklist_count?: number;
  team?: { id: number; user_name: string; role: string }[];
  findings?: { id: number; title: string; severity: string; status: string }[];
  created_at?: string;
}

interface AuditForm {
  title: string;
  objective: string;
  scope_description: string;
  criteria: string;
  audit_type: AuditType;
  status: AuditStatus;
  planned_start: string;
  planned_end: string;
}

/* ------------------------------------------------------------------ */
/*  Constants                                                          */
/* ------------------------------------------------------------------ */

const AUDIT_TYPES: AuditType[] = ['internal', 'surveillance', 'recertification', 'supplier', 'process', 'follow_up'];
const AUDIT_STATUSES: AuditStatus[] = ['planned', 'in_progress', 'fieldwork', 'reporting', 'management_review', 'closed'];

const statusColors: Record<AuditStatus, string> = {
  planned: 'bg-blue-100 text-blue-800',
  in_progress: 'bg-amber-100 text-amber-800',
  fieldwork: 'bg-purple-100 text-purple-800',
  reporting: 'bg-indigo-100 text-indigo-800',
  management_review: 'bg-orange-100 text-orange-800',
  closed: 'bg-green-100 text-green-800',
};

const typeColors: Record<AuditType, string> = {
  internal: 'bg-slate-100 text-slate-700',
  surveillance: 'bg-cyan-100 text-cyan-700',
  recertification: 'bg-teal-100 text-teal-700',
  supplier: 'bg-rose-100 text-rose-700',
  process: 'bg-violet-100 text-violet-700',
  follow_up: 'bg-yellow-100 text-yellow-700',
};

const emptyForm: AuditForm = {
  title: '',
  objective: '',
  scope_description: '',
  criteria: '',
  audit_type: 'internal',
  status: 'planned',
  planned_start: '',
  planned_end: '',
};

const formatLabel = (s: string) => s.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
const formatDate = (d: string | null) => (d ? new Date(d).toLocaleDateString() : '--');

/* ------------------------------------------------------------------ */
/*  API helpers                                                        */
/* ------------------------------------------------------------------ */

const getCsrfToken = () => document.cookie.match(/(?:^|;\s*)csrf_token=([^;]+)/)?.[1] ?? '';
const authHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem('token')}`,
  'Content-Type': 'application/json',
  'X-CSRF-Token': getCsrfToken(),
});

const API_BASE = '/api/compliance/internal-audits';

async function apiFetch<T>(url: string, opts?: RequestInit): Promise<T> {
  const res = await fetch(url, { headers: authHeaders(), ...opts });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.message || body.error || `Request failed (${res.status})`);
  }
  return res.json();
}

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

const InternalAudits: React.FC = () => {
  const [audits, setAudits] = useState<Audit[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  // Filters
  const [statusFilter, setStatusFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');

  // Expanded row
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [expandedDetail, setExpandedDetail] = useState<Audit | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  // Modal
  const [modalOpen, setModalOpen] = useState(false);
  const [editingAudit, setEditingAudit] = useState<Audit | null>(null);
  const [form, setForm] = useState<AuditForm>({ ...emptyForm });
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState('');

  /* ---- Load list ---- */
  const load = useCallback(async () => {
    setIsLoading(true);
    setError('');
    try {
      const params = new URLSearchParams();
      if (statusFilter) params.set('status', statusFilter);
      if (typeFilter) params.set('audit_type', typeFilter);
      params.set('limit', '50');
      const qs = params.toString();
      const data = await apiFetch<any>(`${API_BASE}${qs ? `?${qs}` : ''}`);
      setAudits(Array.isArray(data) ? data : data.audits ?? data.data ?? []);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setIsLoading(false);
    }
  }, [statusFilter, typeFilter]);

  useEffect(() => { load(); }, [load]);

  /* ---- Load detail on expand ---- */
  const toggleExpand = async (id: number) => {
    if (expandedId === id) {
      setExpandedId(null);
      setExpandedDetail(null);
      return;
    }
    setExpandedId(id);
    setExpandedDetail(null);
    setDetailLoading(true);
    try {
      const detail = await apiFetch<Audit>(`${API_BASE}/${id}`);
      setExpandedDetail(detail);
    } catch {
      // silently fail — row just won't show detail
    } finally {
      setDetailLoading(false);
    }
  };

  /* ---- Open create / edit modal ---- */
  const openCreate = () => {
    setEditingAudit(null);
    setForm({ ...emptyForm });
    setFormError('');
    setModalOpen(true);
  };

  const openEdit = (audit: Audit) => {
    setEditingAudit(audit);
    setForm({
      title: audit.title,
      objective: audit.objective || '',
      scope_description: audit.scope_description || '',
      criteria: audit.criteria || '',
      audit_type: audit.audit_type,
      status: audit.status,
      planned_start: audit.planned_start ? audit.planned_start.slice(0, 10) : '',
      planned_end: audit.planned_end ? audit.planned_end.slice(0, 10) : '',
    });
    setFormError('');
    setModalOpen(true);
  };

  /* ---- Submit form ---- */
  const handleSubmit = async () => {
    if (!form.title.trim()) {
      setFormError('Title is required.');
      return;
    }
    setSubmitting(true);
    setFormError('');
    try {
      const body = {
        ...form,
        planned_start: form.planned_start || null,
        planned_end: form.planned_end || null,
      };
      if (editingAudit) {
        await apiFetch(`${API_BASE}/${editingAudit.id}`, { method: 'PUT', body: JSON.stringify(body) });
      } else {
        await apiFetch(API_BASE, { method: 'POST', body: JSON.stringify(body) });
      }
      setModalOpen(false);
      load();
    } catch (e: any) {
      setFormError(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  const setField = <K extends keyof AuditForm>(key: K, value: AuditForm[K]) =>
    setForm(prev => ({ ...prev, [key]: value }));

  /* ---- Render ---- */
  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-emerald-100 rounded-xl">
            <ClipboardCheck size={24} className="text-emerald-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Internal Audits</h1>
            <p className="text-sm text-gray-500">Plan, execute, and track internal audit engagements</p>
          </div>
        </div>
        <div className="flex gap-2">
          <button
            onClick={load}
            className="flex items-center gap-1 px-3 py-2 text-sm text-gray-600 border rounded-lg hover:bg-gray-50"
          >
            <RefreshCw size={16} /> Refresh
          </button>
          <button
            onClick={openCreate}
            className="flex items-center gap-1 px-4 py-2 text-sm text-white bg-emerald-600 rounded-lg hover:bg-emerald-700 shadow-sm"
          >
            <Plus size={16} /> New Audit
          </button>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">
          <AlertTriangle size={16} /> {error}
        </div>
      )}

      {/* Filter bar */}
      <div className="flex flex-wrap items-center gap-3 bg-white border rounded-xl p-4">
        <Search size={16} className="text-gray-400" />
        <select
          value={statusFilter}
          onChange={e => setStatusFilter(e.target.value)}
          className="border rounded-lg px-3 py-2 text-sm bg-white"
        >
          <option value="">All Statuses</option>
          {AUDIT_STATUSES.map(s => (
            <option key={s} value={s}>{formatLabel(s)}</option>
          ))}
        </select>
        <select
          value={typeFilter}
          onChange={e => setTypeFilter(e.target.value)}
          className="border rounded-lg px-3 py-2 text-sm bg-white"
        >
          <option value="">All Types</option>
          {AUDIT_TYPES.map(t => (
            <option key={t} value={t}>{formatLabel(t)}</option>
          ))}
        </select>
        {(statusFilter || typeFilter) && (
          <button
            onClick={() => { setStatusFilter(''); setTypeFilter(''); }}
            className="text-xs text-gray-500 hover:text-gray-700 underline"
          >
            Clear filters
          </button>
        )}
      </div>

      {/* Loading */}
      {isLoading ? (
        <div className="flex justify-center py-16">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600" />
        </div>
      ) : audits.length === 0 ? (
        <div className="py-16 bg-white border rounded-xl">
          <ClipboardCheck size={48} className="mx-auto text-gray-300 mb-4" />
          <p className="text-gray-500 text-lg font-medium">No audits found</p>
          <p className="text-gray-400 text-sm mt-1">Create your first internal audit to get started.</p>
          <button
            onClick={openCreate}
            className="mt-4 inline-flex items-center gap-1 px-4 py-2 text-sm text-white bg-emerald-600 rounded-lg hover:bg-emerald-700"
          >
            <Plus size={16} /> New Audit
          </button>
        </div>
      ) : (
        /* Audit list */
        <div className="space-y-3">
          {audits.map(audit => {
            const isExpanded = expandedId === audit.id;
            return (
              <div key={audit.id} className="bg-white border rounded-xl overflow-hidden shadow-sm hover:shadow transition-shadow">
                {/* Row */}
                <div
                  className="flex items-center gap-4 px-5 py-4 cursor-pointer"
                  onClick={() => toggleExpand(audit.id)}
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold text-gray-900 truncate">{audit.title}</span>
                      <span className={`inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full ${typeColors[audit.audit_type]}`}>
                        {formatLabel(audit.audit_type)}
                      </span>
                    </div>
                    <div className="flex items-center gap-4 mt-1 text-xs text-gray-500">
                      {audit.lead_auditor_name && (
                        <span className="flex items-center gap-1">
                          <User size={12} /> {audit.lead_auditor_name}
                        </span>
                      )}
                      <span className="flex items-center gap-1">
                        <Calendar size={12} /> {formatDate(audit.planned_start)} - {formatDate(audit.planned_end)}
                      </span>
                    </div>
                  </div>

                  <span className={`inline-flex items-center px-2.5 py-1 text-xs font-medium rounded-full whitespace-nowrap ${statusColors[audit.status]}`}>
                    {formatLabel(audit.status)}
                  </span>

                  {audit.findings_count !== undefined && audit.findings_count > 0 && (
                    <span className="text-xs text-gray-500 whitespace-nowrap">
                      {audit.findings_count} finding{audit.findings_count !== 1 ? 's' : ''}
                    </span>
                  )}

                  <button className="text-gray-400 hover:text-gray-600 flex-shrink-0">
                    {isExpanded ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                  </button>
                </div>

                {/* Expanded detail */}
                {isExpanded && (
                  <div className="border-t bg-slate-50 px-5 py-4">
                    {detailLoading ? (
                      <div className="flex justify-center py-6">
                        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-emerald-600" />
                      </div>
                    ) : expandedDetail ? (
                      <div className="space-y-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          {expandedDetail.objective && (
                            <div>
                              <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Objective</p>
                              <p className="text-sm text-gray-700">{expandedDetail.objective}</p>
                            </div>
                          )}
                          {expandedDetail.scope_description && (
                            <div>
                              <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Scope</p>
                              <p className="text-sm text-gray-700">{expandedDetail.scope_description}</p>
                            </div>
                          )}
                          {expandedDetail.criteria && (
                            <div>
                              <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Criteria</p>
                              <p className="text-sm text-gray-700">{expandedDetail.criteria}</p>
                            </div>
                          )}
                        </div>

                        {/* Audit team */}
                        {expandedDetail.team && expandedDetail.team.length > 0 && (
                          <div>
                            <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">Audit Team</p>
                            <div className="flex flex-wrap gap-2">
                              {expandedDetail.team.map(m => (
                                <span key={m.id} className="inline-flex items-center gap-1 px-2.5 py-1 bg-white border rounded-full text-xs text-gray-700">
                                  <User size={12} className="text-gray-400" />
                                  {m.user_name}
                                  <span className="text-gray-400">({m.role})</span>
                                </span>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Findings */}
                        {expandedDetail.findings && expandedDetail.findings.length > 0 && (
                          <div>
                            <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">
                              Findings ({expandedDetail.findings.length})
                            </p>
                            <div className="space-y-1">
                              {expandedDetail.findings.map(f => (
                                <div key={f.id} className="flex items-center justify-between bg-white border rounded-lg px-3 py-2 text-sm">
                                  <span className="text-gray-800">{f.title}</span>
                                  <div className="flex items-center gap-2">
                                    <span className={`px-2 py-0.5 text-xs rounded-full ${
                                      f.severity === 'critical' ? 'bg-red-100 text-red-700' :
                                      f.severity === 'major' ? 'bg-orange-100 text-orange-700' :
                                      f.severity === 'minor' ? 'bg-yellow-100 text-yellow-700' :
                                      'bg-gray-100 text-gray-600'
                                    }`}>
                                      {f.severity}
                                    </span>
                                    <span className="text-xs text-gray-500">{formatLabel(f.status)}</span>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        <div className="flex justify-end">
                          <button
                            onClick={e => { e.stopPropagation(); openEdit(expandedDetail); }}
                            className="px-4 py-2 text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg hover:bg-emerald-100"
                          >
                            Edit Audit
                          </button>
                        </div>
                      </div>
                    ) : (
                      <p className="text-sm text-gray-400 text-center py-4">Unable to load audit details.</p>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* ---- Create / Edit Modal ---- */}
      {modalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
            {/* Modal header */}
            <div className="flex items-center justify-between px-6 py-4 border-b">
              <h2 className="text-lg font-semibold text-gray-900">
                {editingAudit ? 'Edit Audit' : 'Create New Audit'}
              </h2>
              <button onClick={() => setModalOpen(false)} className="text-gray-400 hover:text-gray-600">
                <X size={20} />
              </button>
            </div>

            {/* Modal body */}
            <div className="px-6 py-5 space-y-4">
              {formError && (
                <div className="p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">{formError}</div>
              )}

              {/* Title */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Title *</label>
                <input
                  type="text"
                  value={form.title}
                  onChange={e => setField('title', e.target.value)}
                  className="w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none"
                  placeholder="e.g. Q2 2026 ISO 27001 Internal Audit"
                />
              </div>

              {/* Type & Status */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Audit Type</label>
                  <select
                    value={form.audit_type}
                    onChange={e => setField('audit_type', e.target.value as AuditType)}
                    className="w-full border rounded-lg px-3 py-2 text-sm bg-white"
                  >
                    {AUDIT_TYPES.map(t => (
                      <option key={t} value={t}>{formatLabel(t)}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                  <select
                    value={form.status}
                    onChange={e => setField('status', e.target.value as AuditStatus)}
                    className="w-full border rounded-lg px-3 py-2 text-sm bg-white"
                  >
                    {AUDIT_STATUSES.map(s => (
                      <option key={s} value={s}>{formatLabel(s)}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Dates */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Planned Start</label>
                  <input
                    type="date"
                    value={form.planned_start}
                    onChange={e => setField('planned_start', e.target.value)}
                    className="w-full border rounded-lg px-3 py-2 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Planned End</label>
                  <input
                    type="date"
                    value={form.planned_end}
                    onChange={e => setField('planned_end', e.target.value)}
                    className="w-full border rounded-lg px-3 py-2 text-sm"
                  />
                </div>
              </div>

              {/* Objective */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Objective</label>
                <textarea
                  value={form.objective}
                  onChange={e => setField('objective', e.target.value)}
                  rows={2}
                  className="w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none resize-none"
                  placeholder="What is the purpose of this audit?"
                />
              </div>

              {/* Scope */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Scope Description</label>
                <textarea
                  value={form.scope_description}
                  onChange={e => setField('scope_description', e.target.value)}
                  rows={2}
                  className="w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none resize-none"
                  placeholder="Departments, processes, or areas in scope"
                />
              </div>

              {/* Criteria */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Criteria</label>
                <textarea
                  value={form.criteria}
                  onChange={e => setField('criteria', e.target.value)}
                  rows={2}
                  className="w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none resize-none"
                  placeholder="Standards, policies, or regulations to audit against"
                />
              </div>
            </div>

            {/* Modal footer */}
            <div className="flex items-center justify-end gap-3 px-6 py-4 border-t bg-gray-50 rounded-b-xl">
              <button
                onClick={() => setModalOpen(false)}
                className="px-4 py-2 text-sm text-gray-600 border rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                disabled={submitting}
                className="px-5 py-2 text-sm text-white bg-emerald-600 rounded-lg hover:bg-emerald-700 disabled:opacity-50 shadow-sm"
              >
                {submitting ? 'Saving...' : editingAudit ? 'Update Audit' : 'Create Audit'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InternalAudits;
